import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import './status-pages.css';

type Component = { kind: 'monitor' | 'service'; sourceId: string; label: string };
type Notice = { kind: 'incident' | 'maintenance'; sourceId: string; title: string; body: string };
type Draft = {
  id?: string;
  revision?: number;
  slug: string;
  title: string;
  description: string;
  visibility: 'private' | 'public';
  customBranding: boolean;
  components: Component[];
  notices: Notice[];
};
type Source = { id: string; kind: Component['kind'] | Notice['kind']; label: string };
type View = {
  title: string;
  description: string;
  visibility: string;
  checkedAt: string;
  state: string;
  branding: { displayName: string; primaryColor: string; showPoweredByAot: boolean };
  components: { id: string; label: string; state: string; observedAt: string | null }[];
  notices: {
    id: string;
    kind: string;
    title: string;
    body: string;
    state: string;
    startsAt?: string;
    endsAt?: string;
    timeZone?: string;
    recurrence?: string;
  }[];
};
const empty: Draft = {
  slug: '',
  title: '',
  description: '',
  visibility: 'private',
  customBranding: false,
  components: [],
  notices: [],
};
const stateLabel: Record<string, string> = {
  operational: 'Operational',
  degraded: 'Degraded service',
  outage: 'Service outage',
  maintenance: 'Under maintenance',
  unknown: 'Status unavailable',
  investigating: 'Investigating',
  resolved: 'Resolved',
  in_progress: 'In progress',
  scheduled: 'Scheduled',
  recurring: 'Recurring schedule',
  completed: 'Completed',
  cancelled: 'Cancelled',
  unavailable: 'Status unavailable',
};
const stamp = (value: string | null | undefined) =>
  value && Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString() : 'Not available';

export function StatusPageContent({ view }: { view: View }) {
  return (
    <article className="published-status" style={{ borderTopColor: view.branding.primaryColor }}>
      <header>
        <p className="status-brand">{view.branding.displayName}</p>
        <h1>{view.title}</h1>
        <p>{view.description}</p>
        {view.visibility === 'private' && (
          <p className="status-private">Private · visible to signed-in workspace members</p>
        )}
      </header>
      <section
        className={'status-overall status-' + view.state}
        aria-label="Overall service status"
      >
        <strong>{stateLabel[view.state] ?? 'Status unavailable'}</strong>
        <span>Updated {stamp(view.checkedAt)}</span>
      </section>
      <section aria-label="Published services">
        <h2>Services</h2>
        {!view.components.length ? (
          <p>No services selected.</p>
        ) : (
          view.components.map((c) => (
            <article className="status-component" key={c.id}>
              <div>
                <h3>{c.label}</h3>
                <small>Last observation: {stamp(c.observedAt)}</small>
              </div>
              <span className={'status-state status-' + c.state}>
                {stateLabel[c.state] ?? 'Status unavailable'}
              </span>
            </article>
          ))
        )}
      </section>
      <section aria-label="Published notices">
        <h2>Incidents and maintenance</h2>
        {!view.notices.length ? (
          <p>No notices have been published.</p>
        ) : (
          view.notices.map((n) => (
            <article className="status-notice" key={n.id}>
              <p className="status-kicker">
                {n.kind === 'maintenance' ? 'Maintenance' : 'Incident'} ·{' '}
                {stateLabel[n.state] ?? 'Status unavailable'}
              </p>
              <h3>{n.title}</h3>
              <p>{n.body}</p>
              {n.kind === 'maintenance' && (
                <p className="status-schedule">
                  {stamp(n.startsAt)} – {stamp(n.endsAt)}
                  <br />
                  Times displayed in your browser timezone. Schedule timezone:{' '}
                  {n.timeZone ?? 'Unavailable'}.
                  {n.recurrence && n.recurrence !== 'none' ? ' Repeats ' + n.recurrence + '.' : ''}
                </p>
              )}
            </article>
          ))
        )}
      </section>
      {view.branding.showPoweredByAot && <footer>Powered by AOT OpsAI</footer>}
    </article>
  );
}

export function StatusPageViewer({ slug, privateId }: { slug?: string; privateId?: string }) {
  const path = privateId
    ? '/v1/status-pages/' + encodeURIComponent(privateId) + '/preview'
    : '/v1/status-pages/public/' + encodeURIComponent(slug ?? '');
  const [result, setResult] = useState<{
    path: string;
    view: View | null;
    error: string;
    loading: boolean;
  }>({ path, view: null, error: '', loading: true });
  const requestSequence = useRef(0);
  // Never render another page's content while the selected page is loading.
  const { view, error, loading } =
    result.path === path ? result : { view: null, error: '', loading: true };
  async function load() {
    const sequence = ++requestSequence.current;
    setResult((previous) => ({
      path,
      view: previous.path === path ? previous.view : null,
      error: '',
      loading: true,
    }));
    try {
      const next = await api<View>(path);
      if (sequence === requestSequence.current)
        setResult({ path, view: next, error: '', loading: false });
    } catch (e) {
      if (sequence === requestSequence.current)
        setResult({
          path,
          view: null,
          error:
            e instanceof ApiError && e.status === 401
              ? 'Sign in to the workspace to view this private page.'
              : 'This status page is unavailable.',
          loading: false,
        });
    }
  }
  useEffect(() => {
    void load();
    const timer = setInterval(() => {
      if (!document.hidden) void load();
    }, 60000);
    return () => {
      ++requestSequence.current;
      clearInterval(timer);
    };
  }, [path]);
  return (
    <main className="status-page-shell">
      <nav aria-label="Status page controls">
        <a href="/services?section=status-pages">Workspace</a>
        <button className="button secondary" onClick={() => void load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh status'}
        </button>
      </nav>
      {error ? (
        <section role="alert">
          <h1>Status page unavailable</h1>
          <p>{error}</p>
          <a href="/">Sign in to OpsAI</a>
        </section>
      ) : view ? (
        <StatusPageContent view={view} />
      ) : (
        <p role="status">Loading status…</p>
      )}
    </main>
  );
}

export function StatusPagesManager() {
  const [data, setData] = useState<{
    items: { id: string; title: string; slug: string; visibility: string; revision: number }[];
    canEdit: boolean;
    canBrand: boolean;
    limit: number;
    publicReadBudget?: { limit: number; used: number; remaining: number; resetsAt: string } | null;
  } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null),
    [sources, setSources] = useState<{ components: Source[]; notices: Source[] }>({
      components: [],
      notices: [],
    });
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [review, setReview] = useState(false),
    [search, setSearch] = useState(''),
    [deleting, setDeleting] = useState<string | null>(null);
  async function load() {
    try {
      setData(await api('/v1/status-pages'));
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function loadSources(value = '') {
    try {
      setSources(await api('/v1/status-pages/sources?search=' + encodeURIComponent(value)));
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function edit(id?: string) {
    setBusy(true);
    setError('');
    try {
      setDraft(
        id
          ? await api<Draft>('/v1/status-pages/' + encodeURIComponent(id))
          : { ...empty, components: [], notices: [] },
      );
      setReview(false);
      setSearch('');
      await loadSources();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!draft) return;
    setBusy(true);
    setError('');
    try {
      await api('/v1/status-pages' + (draft.id ? '/' + encodeURIComponent(draft.id) : ''), {
        method: draft.id ? 'PUT' : 'POST',
        body: JSON.stringify(draft),
      });
      setDraft(null);
      setReview(false);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: string, revision: number) {
    setBusy(true);
    try {
      await api('/v1/status-pages/' + encodeURIComponent(id) + '?revision=' + revision, {
        method: 'DELETE',
      });
      setDeleting(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="status-manager">
      <header>
        <p className="status-kicker">CUSTOMER COMMUNICATION</p>
        <h1>Status pages</h1>
        <p>Share selected service health and reviewed incident or maintenance notices.</p>
      </header>
      {error && (
        <div role="alert" className="error-state">
          {error}
          {!data && <button onClick={() => void load()}>Retry</button>}
        </div>
      )}
      {!data && !error && <p role="status">Loading status pages…</p>}
      {data && !draft && (
        <>
          <div className="status-toolbar">
            <p>
              {data.items.length} of {data.limit} pages in this workspace
            </p>
            {data.canEdit && (
              <button
                className="button"
                disabled={busy || data.items.length >= data.limit}
                onClick={() => void edit()}
              >
                Create status page
              </button>
            )}
          </div>
          {data.publicReadBudget && (
            <p>
              {data.publicReadBudget.used.toLocaleString()} of{' '}
              {data.publicReadBudget.limit.toLocaleString()} public status reads used this month.
              Each page load and refresh counts. The allowance resets on{' '}
              {new Date(data.publicReadBudget.resetsAt).toLocaleDateString()}; public delivery
              pauses when it is used up.
            </p>
          )}
          {!data.items.length && (
            <p>
              No status pages yet. Create a private page, then review its contents before
              publishing.
            </p>
          )}
          {data.items.map((p) => (
            <article className="status-page-row" key={p.id}>
              <div>
                <h2>{p.title}</h2>
                <p>
                  {p.visibility === 'public' ? 'Public' : 'Private'} · /status/{p.slug}
                </p>
              </div>
              <div className="status-actions">
                <a
                  className="button secondary"
                  target="_blank"
                  rel="noreferrer"
                  href={p.visibility === 'public' ? '/status/' + p.slug : '/status/private/' + p.id}
                >
                  View page
                </a>
                {data.canEdit && (
                  <>
                    <button
                      className="button secondary"
                      disabled={busy}
                      onClick={() => void edit(p.id)}
                    >
                      Edit {p.title}
                    </button>
                    <button
                      className="button secondary"
                      disabled={busy}
                      onClick={() => setDeleting(p.id)}
                    >
                      Delete {p.title}
                    </button>
                  </>
                )}
              </div>
              {deleting === p.id && (
                <div className="status-delete">
                  <p>
                    Delete this page and its publication selections? Its public address will become
                    unavailable.
                  </p>
                  <button
                    className="button"
                    disabled={busy}
                    onClick={() => void remove(p.id, p.revision)}
                  >
                    Confirm deletion
                  </button>
                  <button className="button secondary" onClick={() => setDeleting(null)}>
                    Keep page
                  </button>
                </div>
              )}
            </article>
          ))}
        </>
      )}
      {draft && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (review) void save();
            else setReview(true);
          }}
        >
          {review ? (
            <section className="status-review">
              <h2>
                Review {draft.visibility === 'public' ? 'public publication' : 'private page'}
              </h2>
              <p>
                {draft.visibility === 'public'
                  ? 'Anyone with this address will be able to read the selected labels, service states and notices.'
                  : 'Only signed-in members of this workspace can view this page.'}
              </p>
              <p>/status/{draft.slug}</p>
              <h3>{draft.title}</h3>
              <p>{draft.description}</p>
              <h3>Selected services</h3>
              {draft.components.length ? (
                draft.components.map((c) => <p key={c.kind + c.sourceId}>{c.label}</p>)
              ) : (
                <p>No services selected. Overall status will be unavailable.</p>
              )}
              <h3>Published notices</h3>
              {draft.notices.length ? (
                draft.notices.map((n) => (
                  <article key={n.kind + n.sourceId}>
                    <h4>{n.title}</h4>
                    <p>{n.body}</p>
                  </article>
                ))
              ) : (
                <p>No notices selected.</p>
              )}
              <p>
                {draft.customBranding
                  ? 'Use entitled workspace branding.'
                  : 'Use AOT OpsAI branding.'}
              </p>
            </section>
          ) : (
            <>
              <div className="status-fields">
                <label>
                  Page title
                  <input
                    required
                    maxLength={120}
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  />
                </label>
                <label>
                  Public address name
                  <input
                    required
                    minLength={3}
                    maxLength={48}
                    pattern="[a-z0-9][a-z0-9-]{1,46}[a-z0-9]"
                    placeholder="company-status"
                    value={draft.slug}
                    onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                  />
                  <small>3–48 lowercase letters, numbers or hyphens.</small>
                </label>
                <label>
                  Description
                  <textarea
                    maxLength={1000}
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  />
                </label>
                <label>
                  Visibility
                  <select
                    value={draft.visibility}
                    onChange={(e) =>
                      setDraft({ ...draft, visibility: e.target.value as Draft['visibility'] })
                    }
                  >
                    <option value="private">Private — workspace members</option>
                    <option value="public">Public — anyone with the address</option>
                  </select>
                </label>
                {data?.canBrand && (
                  <label className="status-check">
                    <input
                      type="checkbox"
                      checked={draft.customBranding}
                      onChange={(e) => setDraft({ ...draft, customBranding: e.target.checked })}
                    />
                    Use workspace branding
                  </label>
                )}
              </div>
              <section>
                <h2>Select services</h2>
                <p>
                  Choose up to 25 HTTP/DNS monitors or business services. Review the labels shown to
                  readers.
                </p>
                <div className="status-source-search">
                  <label>
                    Find sources
                    <input value={search} onChange={(e) => setSearch(e.target.value)} />
                  </label>
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => void loadSources(search)}
                  >
                    Search sources
                  </button>
                </div>
                <p className="status-hint">
                  Up to 100 matches per source type. Narrow your search if a source is missing.
                </p>
                {sources.components.map((s) => {
                  const selected = draft.components.some(
                    (c) => c.kind === s.kind && c.sourceId === s.id,
                  );
                  return (
                    <label className="status-check" key={s.kind + s.id}>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!selected && draft.components.length >= 25}
                        onChange={() =>
                          setDraft({
                            ...draft,
                            components: selected
                              ? draft.components.filter(
                                  (c) => c.kind !== s.kind || c.sourceId !== s.id,
                                )
                              : [
                                  ...draft.components,
                                  {
                                    kind: s.kind as Component['kind'],
                                    sourceId: s.id,
                                    label: s.label,
                                  },
                                ],
                          })
                        }
                      />
                      {s.label} <small>({s.kind})</small>
                    </label>
                  );
                })}
                {!sources.components.length && <p>No matching supported sources.</p>}
                {draft.components.map((c, i) => (
                  <div className="status-selection" key={c.kind + c.sourceId}>
                    <label>
                      Published service label {i + 1}
                      <input
                        required
                        maxLength={120}
                        value={c.label}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            components: draft.components.map((v, j) =>
                              j === i ? { ...v, label: e.target.value } : v,
                            ),
                          })
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="button secondary"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          components: draft.components.filter((_, j) => j !== i),
                        })
                      }
                    >
                      Remove service {i + 1}
                    </button>
                  </div>
                ))}
              </section>
              <section>
                <h2>Publish notices</h2>
                <p>
                  Internal incident and maintenance text stays private. Write the exact text readers
                  should see for each selected notice.
                </p>
                {sources.notices.map((s) => {
                  const selected = draft.notices.some(
                    (n) => n.kind === s.kind && n.sourceId === s.id,
                  );
                  return (
                    <label className="status-check" key={s.kind + s.id}>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!selected && draft.notices.length >= 20}
                        onChange={() =>
                          setDraft({
                            ...draft,
                            notices: selected
                              ? draft.notices.filter(
                                  (n) => n.kind !== s.kind || n.sourceId !== s.id,
                                )
                              : [
                                  ...draft.notices,
                                  {
                                    kind: s.kind as Notice['kind'],
                                    sourceId: s.id,
                                    title: '',
                                    body: '',
                                  },
                                ],
                          })
                        }
                      />
                      {s.label} <small>({s.kind})</small>
                    </label>
                  );
                })}
                {!sources.notices.length && <p>No matching incident or maintenance sources.</p>}
                {draft.notices.map((n, i) => (
                  <div className="status-notice-editor" key={n.kind + n.sourceId}>
                    <label>
                      Public notice title {i + 1}
                      <input
                        required
                        maxLength={160}
                        value={n.title}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            notices: draft.notices.map((v, j) =>
                              j === i ? { ...v, title: e.target.value } : v,
                            ),
                          })
                        }
                      />
                    </label>
                    <label>
                      Public notice text {i + 1}
                      <textarea
                        maxLength={2000}
                        value={n.body}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            notices: draft.notices.map((v, j) =>
                              j === i ? { ...v, body: e.target.value } : v,
                            ),
                          })
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="button secondary"
                      onClick={() =>
                        setDraft({ ...draft, notices: draft.notices.filter((_, j) => j !== i) })
                      }
                    >
                      Remove notice {i + 1}
                    </button>
                  </div>
                ))}
              </section>
            </>
          )}
          {draft.visibility === 'public' && !draft.components.length && (
            <p role="alert">Select at least one service before publishing.</p>
          )}
          <div className="status-actions">
            {review && (
              <button
                type="button"
                className="button secondary"
                disabled={busy}
                onClick={() => setReview(false)}
              >
                Back to editing
              </button>
            )}
            <button
              className="button"
              disabled={busy || (draft.visibility === 'public' && !draft.components.length)}
            >
              {busy
                ? 'Saving…'
                : review
                  ? draft.visibility === 'public'
                    ? 'Publish status page'
                    : 'Save private page'
                  : 'Review page'}
            </button>
            <button
              type="button"
              className="button secondary"
              disabled={busy}
              onClick={() => {
                setDraft(null);
                setReview(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
